import type { AuthTokens } from './cookieUtils'
import { getSecondsUntilExpiry, setTokens } from './cookieUtils'
import { refreshAccessToken } from './authApi'
import {
  BASE_BACKOFF_MS,
  MAX_REFRESH_RETRIES,
  REFRESH_THRESHOLD_SECONDS,
} from './constants'

type QueueItem = {
  resolve: (token: string) => void
  reject: (err: Error) => void
}

type InterceptorConfig = {
  getAccessToken: () => string | null
  getRefreshToken: () => string | null
  onRefreshSuccess: (tokens: AuthTokens) => void
  onRefreshFailure: () => void
}

async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function refreshWithBackoff(
  refreshToken: string,
  onSuccess: (tokens: AuthTokens) => void,
): Promise<string> {
  for (let attempt = 0; attempt < MAX_REFRESH_RETRIES; attempt++) {
    try {
      const tokens = await refreshAccessToken(refreshToken)
      setTokens(tokens)
      onSuccess(tokens)
      return tokens.access_token
    } catch (err) {
      if (attempt < MAX_REFRESH_RETRIES - 1) {
        await sleep(BASE_BACKOFF_MS * Math.pow(2, attempt))
      } else {
        throw err
      }
    }
  }
  throw new Error('Max refresh retries exceeded')
}

function isInternalRequest(input: RequestInfo | URL): boolean {
  const href =
    typeof input === 'string'
      ? input
      : input instanceof URL
      ? input.href
      : input.url
  if (!href) {
    return true
  }
  try {
    const url = new URL(href, window.location.origin)
    return url.origin === window.location.origin
  } catch {
    return true // relative URL — same origin
  }
}

function tokenNeedsRefresh(accessToken: string): boolean {
  return getSecondsUntilExpiry(accessToken) <= REFRESH_THRESHOLD_SECONDS
}

export function setupFetchInterceptor(config: InterceptorConfig): () => void {
  const originalFetch = window.fetch.bind(window)

  // Encapsulate mutable refresh state per interceptor instance (not module-level)
  let isRefreshing = false
  let requestQueue: QueueItem[] = []

  function flushQueue(token: string): void {
    requestQueue.forEach(({ resolve }) => resolve(token))
    requestQueue = []
  }

  function rejectQueue(err: Error): void {
    requestQueue.forEach(({ reject }) => reject(err))
    requestQueue = []
  }

  async function acquireRefreshLock(): Promise<string> {
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        requestQueue.push({ resolve, reject })
      })
    }

    isRefreshing = true

    try {
      const refreshToken = config.getRefreshToken()
      if (!refreshToken) {
        throw new Error('No refresh token available')
      }

      const newToken = await refreshWithBackoff(
        refreshToken,
        config.onRefreshSuccess,
      )
      flushQueue(newToken)
      return newToken
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Refresh failed')
      rejectQueue(error)
      config.onRefreshFailure()
      throw error
    } finally {
      isRefreshing = false
    }
  }

  window.fetch = async function interceptedFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    // Only inject auth header for same-origin requests — prevents token leakage
    if (!isInternalRequest(input)) {
      return originalFetch(input, init)
    }

    let accessToken = config.getAccessToken()

    if (accessToken && tokenNeedsRefresh(accessToken)) {
      try {
        accessToken = await acquireRefreshLock()
      } catch {
        // Refresh failed — onRefreshFailure was called; proceed without token
        return originalFetch(input, init)
      }
    }

    if (!accessToken) {
      return originalFetch(input, init)
    }

    const headers = new Headers(init?.headers)
    if (!headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${accessToken}`)
    }

    return originalFetch(input, { ...init, headers })
  }

  return function cleanup() {
    window.fetch = originalFetch
    // Reject pending queue items to prevent dangling promises
    rejectQueue(new Error('Auth interceptor cleanup'))
    isRefreshing = false
  }
}
